// cSpell:ignore Huurder
// Pure helpers behind the cost and settlement lists (CostsTable, CostsTab,
// CostsOverview, the settlement drawer). Kept apart from hooks.ts so they can
// be unit-tested without the redux wiring.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { uniq, uniqBy } from "lodash";
import { withBilledTarget } from "./chargeTarget.utils";
import { isSettledWithoutOwnTenants } from "./costOwnPart.utils";
import { roundCents } from "./money.utils";
import {
  refusalSettlementRef,
  settlementRefName,
} from "./settlementName.utils";
import {
  IActionStatusMaps,
  openPaymentsList,
  settlementOpenPayments,
  settlementPaidTenants,
} from "./settlementPaymentStatus.utils";
import {
  allSplitsPaid,
  CATEGORY_LABELS_NL,
  Invoice,
  paidCount,
  PaymentSplit,
  requestedCount,
  Settlement,
} from "./types";

// ----- Paging -----

// One row per id, whatever the paging returned: the settlement wizard, the
// engine and the double-settlement guard all assume ids are unique.
export const uniqueById = <TItem extends { id: string | number }>(
  items?: TItem[],
): TItem[] => uniqBy(items ?? [], item => String(item.id));

// ----- Loading -----

// The last fetch of a list failed (and no new one is running). The list may
// still hold what an earlier fetch returned, which is no longer reliable.
export const hasLoadFailed = (params: {
  isFetching: boolean;
  fetchError?: unknown;
}): boolean => !params.isFetching && !!params.fetchError;

export type TListState = "load_failed" | "list" | "loading" | "empty";

// What a cost or settlement list shows. A failed fetch says so, also over
// rows from an earlier fetch: read as "no costs" (or as the old list) it let
// the broker settle or delete on a wrong picture.
export const getListState = (params: {
  isLoading: boolean;
  loadFailed: boolean;
  count: number;
}): TListState => {
  const { isLoading, loadFailed, count } = params;
  if (loadFailed) return "load_failed";
  if (count > 0) return "list";
  if (isLoading) return "loading";
  return "empty";
};

// ----- Cost status -----

// The one status a cost row shows. The list chip and the status filter both
// read it, so a filter returns exactly the rows whose chip carries its label.
export type TCostListStatus =
  | "owner"
  | "paid"
  | "partially_paid"
  | "invited"
  | "partially_invited"
  | "in_settlement"
  | "partly_settled"
  | "to_request"
  | "to_settle";

export const costListStatus = (params: {
  invoice: Invoice;
  settled: boolean;
  // A departure settlement settled one lease's share of the cost and no
  // regular settlement settled the rest (see costSettlementState.utils).
  partlySettled?: boolean;
}): TCostListStatus => {
  const { settled, partlySettled = false } = params;
  // Read the way the settlement bills the cost, like the list's charged-to
  // column and the cost detail: an older ownership cost that still names the
  // tenant is the owner's, so it is not "to settle" for the tenant either.
  const invoice = withBilledTarget(params.invoice);
  // A cost fully for the owner has no tenant payment: it only shows that it
  // went into a settlement.
  if (invoice.target === "owner") return settled ? "in_settlement" : "owner";
  if (allSplitsPaid(invoice) || !!invoice.paidAt) return "paid";
  // An owner's own part of a group cost that charged none of their tenants
  // has no tenant payment of theirs either: not "invited", as the other
  // owners' tenants were.
  if (isSettledWithoutOwnTenants(invoice)) {
    return settled ? "in_settlement" : "owner";
  }

  // Direct and shared costs track the tenant-side payment. The splits' links
  // say whether it was asked; the cost-level date only for a cost from before
  // splits, as it outlives a payment that was withdrawn since.
  if (invoice.target === "direct" || invoice.target === "shared") {
    const splitCount = invoice.paymentSplits?.length ?? 0;
    if (splitCount > 1) {
      if (paidCount(invoice) > 0) return "partially_paid";
      const requested = requestedCount(invoice);
      if (requested === splitCount) return "invited";
      if (requested > 0) return "partially_invited";
    } else if (
      splitCount === 1
        ? requestedCount(invoice) > 0
        : !!invoice.paymentRequestedAt
    ) {
      return "invited";
    }
  }

  // A cost in a confirmed settlement is no longer "to settle": the settlement
  // tracks the tenant payment from then on.
  if (settled) return "in_settlement";
  // A cost charged to the tenant directly never goes into a settlement: until
  // its payment is asked, the broker still has to ask it.
  if (invoice.target === "direct") return "to_request";
  // A departed tenant's share is settled; the rest still waits for the
  // settlement of the unit or the group.
  return partlySettled ? "partly_settled" : "to_settle";
};

export enum ECostStatusFilter {
  All = "",
  ToRequest = "to_request",
  ToSettle = "to_settle",
  Invited = "invited",
  InSettlement = "in_settlement",
  Paid = "paid",
}

// Invitations that are out and not paid in full all wait for the tenant, so
// they share one filter. A cost partly settled at a departure still waits for
// a settlement of the rest. An owner-only cost belongs to no status at all.
const STATUSES_BY_FILTER: Record<ECostStatusFilter, TCostListStatus[]> = {
  [ECostStatusFilter.All]: [],
  [ECostStatusFilter.ToRequest]: ["to_request"],
  [ECostStatusFilter.ToSettle]: ["to_settle", "partly_settled"],
  [ECostStatusFilter.Invited]: [
    "invited",
    "partially_invited",
    "partially_paid",
  ],
  [ECostStatusFilter.InSettlement]: ["in_settlement"],
  [ECostStatusFilter.Paid]: ["paid"],
};

export const costMatchesStatusFilter = (params: {
  status: TCostListStatus;
  filter: ECostStatusFilter;
}): boolean => {
  const { status, filter } = params;
  if (filter === ECostStatusFilter.All) return true;
  return STATUSES_BY_FILTER[filter].includes(status);
};

// ----- Search -----

// Free-text search over what a row shows: the cost's names and its property
// (on the portfolio view the address is what a broker searches for most).
export const costMatchesSearch = (params: {
  invoice: Invoice;
  query: string;
  propertyLabel: string;
}): boolean => {
  const { invoice, query, propertyLabel } = params;
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [
    invoice.supplier,
    invoice.description,
    CATEGORY_LABELS_NL[invoice.category],
    invoice.categoryOther,
    propertyLabel,
  ].some(text => text?.toLowerCase().includes(needle));
};

// ----- Settlements on a unit page -----

// Newest first: confirmedAt is a calendar day, so several settlements of the
// same day keep their creation order (newest first too).
export const sortSettlementsNewestFirst = (
  settlements: Settlement[],
): Settlement[] =>
  [...settlements].sort(
    (a, b) =>
      (b.confirmedAt ?? b.createdAt ?? "").localeCompare(
        a.confirmedAt ?? a.createdAt ?? "",
      ) || (b.createdAt ?? "").localeCompare(a.createdAt ?? ""),
  );

// The confirmed settlements a costs tab lists, newest first (drafts are not
// listed). Each page lists its own. A unit inside a building also lists the
// building's settlements that bill its tenants or its owner: they are made
// (and deleted) on the building, but without them the unit's page showed "no
// settlements" while its tenant had a payment request from one. A building
// also lists the settlements of its units, interim ones and departures
// (Q6 of 2026-10-16): read-only there, they are managed on their unit.
export const listedSettlementsOf = (params: {
  page: "group" | "unit";
  // The page's own settlements.
  own: Settlement[];
  // The settlements of the page's whole building: on a unit its own and its
  // building's, on a building its own and its units'.
  crossLevel: Settlement[];
  groupId?: string;
  unitId?: string;
  parentGroupId?: string;
  // On a building: its units.
  unitIds?: string[];
}): Settlement[] => {
  const { page, crossLevel, groupId, unitId, parentGroupId } = params;
  const own = params.own.filter(s => s.status === "confirmed");
  const ownIds = new Set(own.map(s => s.id));
  const isListed = (s: Settlement) => {
    if (s.status !== "confirmed" || ownIds.has(s.id)) return false;
    if (page === "group") {
      return (
        s.scope === "unit" &&
        !!groupId &&
        (params.unitIds ?? []).includes(String(s.unitId))
      );
    }
    return (
      !!parentGroupId &&
      s.groupId === parentGroupId &&
      ((s.snapshot?.perContract ?? []).some(pc => pc.unitId === unitId) ||
        (s.snapshot?.owners ?? []).some(o =>
          o.unitIds.includes(String(unitId)),
        ))
    );
  };
  return sortSettlementsNewestFirst([
    ...own,
    ...uniqueById(crossLevel.filter(isListed)),
  ]);
};

// Whether a listed settlement is the page's own, which the page manages
// (delete): a building's settlement on a unit and a unit's on a building
// are only read there.
export const isPageOwnSettlement = (params: {
  settlement: Pick<Settlement, "scope" | "groupId" | "unitId">;
  page: "group" | "unit";
  propertyId?: string;
}): boolean => {
  const { settlement, page, propertyId } = params;
  if (!propertyId) return false;
  return page === "group"
    ? settlement.scope === "group" && settlement.groupId === propertyId
    : settlement.scope !== "group" && settlement.unitId === propertyId;
};

// ----- Group costs on a unit page -----

// A group cost asked straight from the tenants (no settlement), seen from one
// of the group's units: only the payments of that unit's tenants, with their
// amount and status. Undefined when none of its payments is on the unit.
// `isUnitPayment` tells from the unit's own payment requests whether a split's
// payment lives on the unit.
export const groupCostPartForUnit = (params: {
  invoice: Invoice;
  isUnitPayment: (split: PaymentSplit) => boolean;
}): Invoice | undefined => {
  const { invoice, isUnitPayment } = params;
  if (invoice.target !== "direct" && invoice.target !== "shared") {
    return undefined;
  }
  const splits = (invoice.paymentSplits ?? []).filter(isUnitPayment);
  if (splits.length === 0) return undefined;

  const requestedDates = splits
    .map(split => split.requestedAt)
    .filter((date): date is string => !!date)
    .sort();
  return {
    ...invoice,
    amountExclVat: roundCents(
      splits.reduce((sum, split) => sum + split.amount, 0),
    ),
    paymentSplits: splits,
    paymentRequestedAt:
      requestedDates.length === splits.length
        ? requestedDates.at(-1)
        : undefined,
  };
};

// ----- Delete refusals -----

interface IServerError {
  message?: string;
  response?: {
    data?: {
      message?: string;
      // rentio-api-next nests its exception under `exception`.
      exception?: {
        message?: string;
        response?: {
          message?: string | string[];
          // A departure's delete refused: the settlements relying on it.
          relyingSettlements?: unknown;
          // A cost's change or delete refused for a departure holding it:
          // that departure (see refusalSettlementRef).
          settlement?: unknown;
          // A settlement's delete refused for a payment: who paid.
          paidBy?: unknown;
        };
      };
    };
  };
}

// Texts rentio-api-next puts in its 409 refusals of a delete.
const SETTLED_COST_REFUSAL = /confirmed settlement/i;
const OPEN_PAYMENT_REFUSAL = /open payment/i;
// "This settlement has paid payments (paid by "A", "B"); a settlement with
// a payment is never deleted".
const PAID_SETTLEMENT_REFUSAL =
  /has paid payments|settlement with a payment is never deleted/i;
// "Settlement "A" relies on…" or "Settlements "A", "B" rely on…".
const RELIED_ON_DEPARTURE_REFUSAL =
  /rel(?:y|ies) on this departure settlement/i;
// A confirm or a departure's delete in the same property holds the lock.
const PROPERTY_BUSY_REFUSAL = /being confirmed right now/i;
// The departure's name comes in quotes; one without a name comes as its id.
const DEPARTURE_COST_REFUSAL = /part of departure settlement (?:"(.+?)"|\S+);/i;
const QUOTED_NAME = /"(.+?)"/g;

// Every text the server sent with an error, wherever the API put it.
export const serverErrorText = (error: unknown): string => {
  const serverError = error as IServerError | undefined;
  const nested = serverError?.response?.data?.exception?.response?.message;
  return [
    serverError?.message,
    serverError?.response?.data?.message,
    serverError?.response?.data?.exception?.message,
    ...(Array.isArray(nested) ? nested : [nested]),
  ]
    .filter(Boolean)
    .join(" ");
};

// The settlements a departure's refused delete names, by their name, else
// their kind and period (settlementRefName). From the
// refusal's relyingSettlements, else from the names its message quotes (a
// settlement without a name is not quoted there); empty when neither says.
const relyingSettlementNames = (error: unknown): string[] => {
  const listed = (error as IServerError | undefined)?.response?.data?.exception
    ?.response?.relyingSettlements;
  if (Array.isArray(listed) && listed.length > 0) {
    return listed.flatMap((item: unknown) => {
      const ref = refusalSettlementRef(item);
      if (ref) return [settlementRefName(ref)];
      const { name } = (item ?? {}) as Record<string, unknown>;
      return typeof name === "string" && name.trim() ? [name.trim()] : [];
    });
  }
  return Array.from(serverErrorText(error).matchAll(QUOTED_NAME), match =>
    String(match[1]),
  );
};

// Who paid, as a settlement's refused delete names them: from the refusal's
// paidBy, else from the names its message quotes.
const paidByNames = (error: unknown): string[] => {
  const listed = (error as IServerError | undefined)?.response?.data?.exception
    ?.response?.paidBy;
  if (Array.isArray(listed) && listed.length > 0) {
    return listed.flatMap((name: unknown) =>
      typeof name === "string" && name.trim() ? [name.trim()] : [],
    );
  }
  return Array.from(serverErrorText(error).matchAll(QUOTED_NAME), match =>
    String(match[1]),
  );
};

// "Lewis Steen", "Lewis Steen en Hugo Huurder", "A, B en C".
const namesText = (names: string[]): string =>
  names.length > 1
    ? `${names.slice(0, -1).join(", ")} ${getLocalizedText("system.and")} ${
        names[names.length - 1]
      }`
    : names.join("");

// A settlement with a payment is never deleted (decision of 2026-10-06),
// nor one with a refund marked as paid back: the explanation, naming who
// paid or was refunded when known, with a plural verb for several (M40).
export const paidSettlementMessage = (names: string[]): string => {
  if (names.length === 0) {
    return getLocalizedText("settlement.delete.blocked_paid_unnamed");
  }
  return getLocalizedText(
    names.length > 1
      ? "settlement.delete.blocked_paid_many"
      : "settlement.delete.blocked_paid",
    { tenants: namesText(names) },
  );
};

// Why a settlement cannot be deleted, checked before the broker is asked to
// confirm (the costs tab, the costs overview and the settlement drawer, F07):
// a tenant payment still open, or a payment (paid, marked paid, a refund paid
// back), since a settlement with a payment is never deleted. Undefined when
// nothing on screen stops it; the server still has the last word.
export const settlementDeleteBlockedMessage = <
  Status extends { status: string },
>(params: {
  settlement: Pick<Settlement, "snapshot" | "scope">;
  statusMaps: IActionStatusMaps<Status>;
}): string | undefined => {
  const { settlement, statusMaps } = params;
  const openPayments = settlementOpenPayments({ settlement, statusMaps });
  if (openPayments.length > 0) {
    return openPaymentMessage({
      kind: "settlement",
      isGroup: settlement.scope === "group",
      openPayments: openPaymentsList(openPayments),
    });
  }
  const paid = settlementPaidTenants({ settlement, statusMaps });
  if (paid.names.length > 0 || paid.hasUnnamed) {
    return paidSettlementMessage(paid.names);
  }
  return undefined;
};

// Why a cost or settlement with an open payment cannot be deleted: a group
// one names where its payments are (the Payments of each unit), with the
// open payments when they are known.
export const openPaymentMessage = (params: {
  kind: "cost" | "settlement";
  isGroup?: boolean;
  openPayments?: string;
}): string => {
  const { kind, isGroup, openPayments } = params;
  if (kind === "cost") {
    return getLocalizedText(
      isGroup
        ? "costs.delete.blocked_open_payment_group"
        : "costs.delete.blocked_open_payment",
    );
  }
  if (!isGroup) {
    return getLocalizedText("settlement.delete.blocked_open_payment");
  }
  return openPayments
    ? getLocalizedText("settlement.delete.blocked_open_payment_group", {
        list: openPayments,
      })
    : getLocalizedText("settlement.delete.blocked_open_payment_group_unlisted");
};

// The server refuses deleting a cost or settlement that a confirmed
// settlement or an open payment still depends on (409). The dashboard checks
// the same rules up front, but its data can be stale (online payments are not
// synced onto the cost), so the server has the last word — this turns its
// refusal into the matching explanation instead of "try again".
export const deleteRefusedMessage = (
  error: unknown,
  kind: "cost" | "settlement",
  // A group cost or settlement: its open payments live on the units, the
  // refusal says so; `openPayments` lists them when the caller knows them
  // (see openPaymentsList).
  options: { isGroup?: boolean; openPayments?: string } = {},
): string => {
  const text = serverErrorText(error);
  const departure = kind === "cost" ? DEPARTURE_COST_REFUSAL.exec(text) : null;
  if (departure) {
    // The departure as the refusal sends it, named in the reader's
    // language; else as its message names it.
    const ref = refusalSettlementRef(
      (error as IServerError | undefined)?.response?.data?.exception?.response
        ?.settlement,
    );
    const name = ref ? settlementRefName(ref) : departure[1];
    return name
      ? getLocalizedText("costs.delete.blocked_departure", { name })
      : getLocalizedText("costs.delete.blocked_departure_unnamed");
  }
  if (kind === "cost" && SETTLED_COST_REFUSAL.test(text)) {
    return getLocalizedText("costs.delete.blocked_settled");
  }
  if (kind === "settlement" && RELIED_ON_DEPARTURE_REFUSAL.test(text)) {
    const names = uniq(relyingSettlementNames(error));
    return names.length > 0
      ? getLocalizedText("settlement.delete.blocked_departure_relied_on", {
          names: names.map(name => `"${name}"`).join(", "),
        })
      : getLocalizedText(
          "settlement.delete.blocked_departure_relied_on_unnamed",
        );
  }
  if (kind === "settlement" && PAID_SETTLEMENT_REFUSAL.test(text)) {
    return paidSettlementMessage(uniq(paidByNames(error)));
  }
  if (kind === "settlement" && PROPERTY_BUSY_REFUSAL.test(text)) {
    return getLocalizedText("settlement.delete.property_busy");
  }
  if (OPEN_PAYMENT_REFUSAL.test(text)) {
    return openPaymentMessage({ kind, ...options });
  }
  return getLocalizedText("settlement.delete_failed");
};

// ----- Apportionment key usage -----

// How many settlements of the group settle with the key: confirmed ones keep
// the weights frozen on confirm, ongoing ones (drafts) follow the key.
export const keyUsage = (params: {
  keyId: string;
  settlements: Settlement[];
}): { confirmed: number; drafts: number } => {
  const { keyId, settlements } = params;
  let confirmed = 0;
  let drafts = 0;
  for (const settlement of settlements) {
    if (settlement.status === "confirmed") {
      const selected = Object.values(settlement.snapshot?.selectedKeyId ?? {});
      if (selected.includes(keyId)) confirmed += 1;
    } else if (
      Object.values(settlement.draftState?.selectedKeyId ?? {}).includes(keyId)
    ) {
      drafts += 1;
    }
  }
  return { confirmed, drafts };
};
